import React from 'react';
import { Button, Menu, Popover, Tooltip, useToasts } from 'freemium-ui';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import FilterViewActions from 'routes/screens/components/CustomFilterView/actions';
import { TopBar } from 'routes/screens/response/style';
import { useGetCurrentAccountUser } from 'routes/screens/settings/hooks';
import Icon from 'shared/components/Icon';
import {
  AccountInfoType,
  ConditionType,
  ConfirmationType,
  FilterViewType,
  SurveyType,
} from 'shared/types';
import { useAccessControl } from 'utils/access-control';
import {
  useExportResponseMutation,
  useFileDownloadMutation,
} from '../../../hooks';
import { getAllFiltersFromFV, getPayloadForFilterView } from '../utils';
import { removeDateFields, urlParamsToObject } from './filter/utils';
import ResponsesBulkActions from './bulk-actions';

type ResponseListHeaderType = {
  surveyInfo: SurveyType;
  surveyResponseCount: string;
  isLoading: boolean;
  filterParams: string;
  localState: Record<string, any>;
  setSurveyOpen: (boolean) => void;
  userId: string | null | undefined;
  accountInfo?: AccountInfoType;
  isFilterConditionsModified?: boolean;
  filteredViews: FilterViewType[];
  setConfirmationData?: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  onClearFilterView: (selectedFilter: FilterViewType) => void;
  onSave: (filterView: any) => void;
  selectedFilter: FilterViewType;
  selectedRows?: string[];
  setSelectedRowIds: (rowIds: Record<string, boolean>) => void;
  setIsResponsesDeleted: (value: Record<string, boolean>) => void;
  isAllSelected?: boolean;
  anchorEl: any;
  setAnchorEl: (value: any) => void;
  editFilterViewRef: any;
  isLoadingSurveyInfo?: boolean;
};

const RESPONSE_EXPORT_TIME_LIMIT = 30;
const RESPONSE_DOWNLOAD_TIME_LIMIT = 180;

const ResponseListHeader = (props: ResponseListHeaderType) => {
  const {
    surveyInfo,
    surveyResponseCount,
    isLoading,
    isLoadingSurveyInfo,
    setSurveyOpen,
    filterParams,
    localState,
    userId,
    accountInfo,
    isFilterConditionsModified,
    onClearFilterView,
    onSave,
    filteredViews,
    selectedFilter,
    selectedRows,
    setConfirmationData,
    setSelectedRowIds,
    setIsResponsesDeleted,
    isAllSelected,
    anchorEl,
    setAnchorEl,
    editFilterViewRef,
  } = props;

  const { t } = useTranslation();
  const { id: surveyId, name: surveyName, type: surveyTheme } = surveyInfo;
  const [exportDisabled, setExportDisabled] = React.useState<boolean>(false);
  const [isDownloadResponsesDisabled, setIsDownloadResponsesDisabled] =
    React.useState<boolean>(false);
  const { access } = useAccessControl();

  const options = [
    {
      id: 'export_all',
      name: 'responses.response_header.export_options.all',
      disabled: exportDisabled,
    },
    ...(filterParams?.length && isFilterConditionsModified
      ? [
          {
            id: 'export_filtered',
            name: 'responses.response_header.export_options.current_filter',
            disabled: exportDisabled,
          },
        ]
      : []),
    ...((surveyInfo?.question_details?.questions || []).filter(
      (question) => question?.type_info?.question_type === 'FILE',
    ).length
      ? [
          {
            id: 'download_all',
            name: 'responses.response_header.download_centre.all',
            disabled: isDownloadResponsesDisabled,
          },
          ...(filterParams?.length
            ? [
                {
                  id: 'download_filtered',
                  name: 'responses.response_header.download_centre.filtered',
                  disabled: isDownloadResponsesDisabled,
                },
              ]
            : []),
        ]
      : []),
  ];

  const [, setToast] = useToasts();
  const { data: accountUserInfo } = useGetCurrentAccountUser(userId || '');
  const { mutate: exportResponse } = useExportResponseMutation({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        setExportDisabled(true);
      }
    },
  });

  const { mutate: downloadFiles } = useFileDownloadMutation({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        setIsDownloadResponsesDisabled(true);
      }
    },
  });

  const optionTranslate = options.map((eOpt) => ({
    ...eOpt,
    name: `${t(`${eOpt.name}`)}`,
  }));

  const handleMenu = (item: { id: string; name: string }) => {
    if (surveyId) {
      const urlConstruct = getAllFiltersFromFV(selectedFilter);
      switch (item?.id) {
        case 'export_all':
          exportResponse({ surveyId, filterParams: urlConstruct });
          break;
        case 'export_filtered':
          exportResponse({ surveyId, filterParams });
          break;
        case 'download_all':
          downloadFiles({ surveyId, filterParams });
          break;
        case 'download_filtered':
          downloadFiles({ surveyId, filterParams });
          break;

        default:
          break;
      }
    }
  };

  React.useEffect(() => {
    if (accountUserInfo?.meta?.response_last_exported_at && surveyId) {
      const isSurveyResponseExported =
        accountUserInfo?.meta?.response_last_exported_at[surveyId];
      if (isSurveyResponseExported) {
        const lastExportedTime = moment(isSurveyResponseExported).tz(
          accountInfo?.timezone || moment.tz.guess(),
          true,
        );
        const currentTime = moment().tz(
          accountInfo?.timezone || moment.tz.guess(),
          true,
        );
        const timeDiffInMinutes = moment
          .duration(currentTime.diff(lastExportedTime))
          .asMinutes();
        setExportDisabled(timeDiffInMinutes <= RESPONSE_EXPORT_TIME_LIMIT);
      }
    }
    if (accountUserInfo?.meta?.last_responses_download_at && surveyId) {
      const isSurveyResponseDownoadedAt =
        accountUserInfo?.meta?.last_responses_download_at[surveyId];
      if (isSurveyResponseDownoadedAt) {
        const lastDownloadedAt = moment(isSurveyResponseDownoadedAt).tz(
          accountInfo?.timezone || moment.tz.guess(),
          true,
        );
        const currentTime = moment().tz(
          accountInfo?.timezone || moment.tz.guess(),
          true,
        );
        const timeDiffInMinutes = moment
          .duration(currentTime.diff(lastDownloadedAt))
          .asMinutes();
        setIsDownloadResponsesDisabled(
          timeDiffInMinutes <= RESPONSE_DOWNLOAD_TIME_LIMIT,
        );
      }
    }
  }, [accountUserInfo, surveyId]);

  const saveFilterView = (filterViewName, isCreate = false) => {
    let filterView;
    if (!isCreate) {
      filterView = filteredViews?.find(
        (filter) => filter.name == filterViewName,
      );
    }
    // Delete date time fields
    const filterObj = urlParamsToObject(filterParams);
    removeDateFields(filterObj);
    const responseConditions: ConditionType[] = getPayloadForFilterView(
      localState,
      filterViewName,
      surveyInfo,
      filterParams,
    );

    // the ternary condition is for checking whether it is for creating or updating a filter
    onSave({
      id:
        !filterViewName && !isCreate
          ? selectedFilter?.id
          : filterView?.id || '',
      name: !filterViewName ? selectedFilter.name : filterViewName,
      conditions: responseConditions,
      created_at: filterView?.created_at || null,
      updated_at: filterView?.updated_at || null,
    });
  };

  return (
    <TopBar
      $isBulkActionsEnabled={
        ((selectedRows || []).length > 0 || isAllSelected) &&
        access.indexOf('response:write') > -1
      }
    >
      {((selectedRows || []).length > 0 || isAllSelected) &&
      access.indexOf('response:write') > -1 ? (
        <ResponsesBulkActions
          selectedRows={selectedRows || []}
          setSelectedRowIds={setSelectedRowIds}
          setConfirmationData={setConfirmationData}
          isLoading={isLoading}
          surveyId={surveyId || ''}
          setIsResponsesDeleted={setIsResponsesDeleted}
        />
      ) : (
        <>
          <div className="left">
            <Tooltip
              label={`${t('tooltip.show_all_surveys')}`}
              position="right"
            >
              <Button
                aria-label={`${t('tooltip.show_all_surveys')}`}
                type="secondary"
                inline
                onClick={(e) => {
                  setSurveyOpen(true);
                  e?.currentTarget?.blur();
                }}
              >
                <Icon
                  glyph="filter"
                  width={14}
                  height={14}
                  viewBox="0 0 16 16"
                />
              </Button>
            </Tooltip>
            {surveyName ? (
              <>
                {selectedFilter?.name ? (
                  <>
                    <h5 className="response-id">{surveyName}</h5>
                    <div style={{ margin: '0 5px' }}>
                      <Icon glyph="nextPageArrow" width={12} height={10} />
                    </div>
                    <h5 className="response-id">
                      {selectedFilter?.name}&nbsp;
                      {!isLoading && surveyResponseCount
                        ? `(${surveyResponseCount})`
                        : null}
                    </h5>
                  </>
                ) : (
                  <h5 className="response-id">
                    {surveyName}&nbsp;
                    {!isLoading && surveyResponseCount
                      ? `(${surveyResponseCount})`
                      : null}
                  </h5>
                )}
                &nbsp;&nbsp;
                {surveyInfo?.type !== 'NONE' && (
                  <div className="custom-tag">{surveyTheme}</div>
                )}
                <div
                  className="filter-view-popover-anchor"
                  aria-describedby="filter-view-edit-popover"
                  ref={editFilterViewRef}
                />
              </>
            ) : (
              <>{isLoadingSurveyInfo && <Skeleton height={24} width={250} />}</>
            )}
          </div>
          {isFilterConditionsModified && (
            <FilterViewActions
              onClearFilterView={onClearFilterView}
              onSave={saveFilterView}
              selectedFilter={selectedFilter}
              filterViews={filteredViews}
              setAnchorEl={setAnchorEl}
            />
          )}
          {!isLoading &&
          Number(surveyResponseCount) &&
          access.includes('response-impexp:write') ? (
            <div className="right">
              <Button
                inline
                aria-describedby="export-response-popover"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                type="secondary"
                disabled={exportDisabled && isDownloadResponsesDisabled}
              >
                <Icon glyph="export" width={12} height={12} fill="none" />
                &nbsp;{t('export')}&nbsp;&nbsp;
                <Icon glyph="arrow-down" width={8} height={8} />
              </Button>
              <Popover
                id="export-response-popover"
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Menu
                  options={optionTranslate}
                  onSelect={(item) => {
                    handleMenu(item);
                    setAnchorEl(null);
                  }}
                  closeMenuCallback={() => setAnchorEl(null)}
                />
              </Popover>
            </div>
          ) : null}
          <Link
            to={`/survey/${surveyId}/edit`}
            className="btn-survey-route"
            target="_blank"
          >
            <Icon fill="#12344d" width="12" height="12" glyph="open-new-tab" />
            {t('responses.table_header.goto_survey')}
          </Link>
        </>
      )}
    </TopBar>
  );
};

export default ResponseListHeader;
